import type { Simplify } from 'type-fest'

/**
 * Removes all properties with nullish values (null or undefined) from an object.
 * Returns a new object with only the properties that have defined values.
 *
 * Keys with always-nullish types are excluded from the result type.
 * Keys with potentially nullish types have their nullish values removed.
 *
 * @example
 * stripNullishValues({ a: 1, b: null, c: undefined, d: 'hello' })
 * // => { a: 1, d: 'hello' }
 *
 * @example
 * // Type inference:
 * const input: { a: string, b: string | null, c: null } = { a: 'hi', b: null, c: null }
 * const result = stripNullishValues(input)
 * // result type: { a: string, b: string }
 * // (c is excluded because it's always null)
 */
export function stripNullishValues<T extends Record<string, unknown>>(
  obj: T
): Simplify<{
  [K in keyof T as NonNullable<T[K]> extends never ? never : K]: NonNullable<T[K]>
}> {
  return Object.fromEntries(Object.entries(obj).filter(([_, value]) => value != null)) as Simplify<{
    [K in keyof T as NonNullable<T[K]> extends never ? never : K]: NonNullable<T[K]>
  }>
}
